<template>
  <modal
    :name="modalName"
    height="auto"
    width="94%"
    :maxWidth="1000"
    :adaptive="true"
    :scrollable="true"
    :clickToClose="false"
    @before-open="aoAbrir"
  >
    <CloseModal :close="close" />
    <Loading :isActive="salvando" />
    <div class="regra-modal__corpo regra-negocio-modal__corpo">
      <div class="regra-modal__cabecalho">
        <div class="regra-modal__icone">
          <fa-icon :icon="iconeTipo" />
        </div>
        <div>
          <h4 class="regra-modal__titulo">{{ titulo }}</h4>
          <span class="regra-modal__subtitulo">{{ subtitulo }}</span>
        </div>
      </div>

      <ol class="regra-modal__etapas">
        <li
          v-for="(etapa, indice) in etapas"
          :key="etapa.chave"
          :class="[
            'regra-modal__etapa',
            { 'regra-modal__etapa--ativa': etapaAtual === indice, 'regra-modal__etapa--erro': mostrarErros && etapaComErro(indice) },
          ]"
        >
          <button
            type="button"
            class="regra-modal__etapa-botao"
            :aria-current="etapaAtual === indice ? 'step' : null"
            @click="irPara(indice)"
          >
            <span class="regra-modal__etapa-numero">
              <fa-icon v-if="mostrarErros && etapaComErro(indice)" icon="exclamation" />
              <template v-else>{{ indice + 1 }}</template>
            </span>
            <span class="regra-modal__etapa-texto">
              <span class="regra-modal__etapa-titulo">{{ etapa.titulo }}</span>
              <span class="regra-modal__etapa-status">{{ statusEtapa(etapa.chave) }}</span>
            </span>
          </button>
        </li>
      </ol>

      <CAlert v-if="mostrarErros && erros.length" color="warning" class="regra-modal__erro">
        Revise os campos antes de salvar:
        <ul class="regra-negocio-modal__lista-erros">
          <li v-for="erro in erros" :key="erro.campo">{{ erro.mensagem }}</li>
        </ul>
      </CAlert>

      <div class="regra-modal__etapa-conteudo">
        <Informacao v-if="etapaAtual === 0" :item="regra" :erros="errosPorCampo" />
        <Regra
          v-else-if="etapaAtual === 1"
          :item="regra"
          :produtos="produtos"
          :dimensoes="dimensoes"
          :promocoes="promocoes"
          :erros="errosPorCampo"
          :erros-carga="errosCarga"
          @recarregar="recarregar"
        />
        <RestricoesRegraNegocio v-else-if="etapaAtual === 2" :item="regra" :rotulos="rotulosRestricoes" @rotulo="registrarRotulo" />
        <Resumo v-else :item="regra" :dimensoes="dimensoes" :promocoes="promocoes" @editar="irPara" />
      </div>
    </div>

    <div class="regra-modal__rodape">
      <CButton variant="outline" color="secondary" class="regra-modal__rodape-espaco" @click="close">Cancelar</CButton>
      <CButton v-if="etapaAtual > 0" variant="outline" color="primary" @click="irPara(etapaAtual - 1)">
        <fa-icon icon="arrow-left" class="mr-1" />
        Voltar
      </CButton>
      <CButton v-if="etapaAtual < etapas.length - 1" color="primary" @click="irPara(etapaAtual + 1)">
        Avançar
        <fa-icon icon="chevron-right" class="ml-1" />
      </CButton>
      <CButton
        v-if="regra.id || etapaAtual === etapas.length - 1"
        color="success"
        :disabled="salvando"
        @click="salvar"
      >
        <fa-icon icon="check" class="mr-1" />
        Salvar
      </CButton>
    </div>
  </modal>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import CloseModal from "@/components/CloseModal.vue";
import Loading from "@/components/Loading.vue";
import Informacao from "./components/Informacao/Informacao.vue";
import Regra from "./components/Regra/Regra.vue";
import RestricoesRegraNegocio from "./components/Restricao/RestricoesRegraNegocio.vue";
import Resumo from "./components/Resumo/Resumo.vue";
import { MODAL_REGRA_NEGOCIO, canaisDaRegra, rotuloTipoRegra, tipoRegra } from "../constantes";
import { mapaDeErros, montarPayload, prepararRegra, validarRegra } from "../helpers/regraNegocio";
import { carregarRotulosRestricoes } from "../helpers/restricoes";

const ETAPAS = [
  { chave: "informacoes", titulo: "Informações" },
  { chave: "regra", titulo: "Regra" },
  { chave: "restricoes", titulo: "Restrições" },
  { chave: "resumo", titulo: "Resumo" },
];

export default {
  name: "editRegraNegocio",
  components: {
    CloseModal,
    Loading,
    Informacao,
    Regra,
    RestricoesRegraNegocio,
    Resumo,
  },

  props: {
    item: { type: Object, required: true },
    close: { type: Function, required: true },
    create: { type: Function, required: true },
    update: { type: Function, required: true },
    salvando: { type: Boolean, default: false },
  },

  data() {
    return {
      modalName: MODAL_REGRA_NEGOCIO,
      etapas: ETAPAS,
      etapaAtual: 0,
      mostrarErros: false,
      regra: prepararRegra(this.item),
      produtos: [],
      dimensoes: [],
      promocoes: [],
      errosCarga: {},
      rotulosRestricoes: {},
    };
  },

  computed: {
    titulo() {
      if (!this.regra.id) return "Nova regra de negócio";

      return this.regra.descricao || `Regra #${this.regra.id}`;
    },

    subtitulo() {
      if (!this.regra.id) return "Defina quando a regra vale, o que ela valida e para quem";

      return `Regra #${this.regra.id} · ${rotuloTipoRegra(this.regra.tipo)}`;
    },

    iconeTipo() {
      return (tipoRegra(this.regra.tipo) || { icone: "clipboard-list" }).icone;
    },

    erros() {
      return validarRegra(this.regra);
    },

    errosPorCampo() {
      if (!this.mostrarErros) return {};

      return mapaDeErros(this.erros);
    },
  },

  watch: {
    etapaAtual(etapa) {
      if (etapa === 2) this.carregarRotulos();
    },

    item(novo) {
      this.regra = prepararRegra(novo);
      this.rotulosRestricoes = {};
      this.etapaAtual = 0;
      this.mostrarErros = false;
    },
  },

  methods: {
    aoAbrir() {
      this.regra = prepararRegra(this.item);
      this.rotulosRestricoes = {};
      this.etapaAtual = 0;
      this.mostrarErros = false;
      this.loadDependencias();
    },

    carregarRotulos() {
      const rotulos = this.rotulosRestricoes;

      carregarRotulosRestricoes(this.regra.restricoes, rotulos, (chave, rotulo) => this.$set(rotulos, chave, rotulo), get);
    },

    registrarRotulo(chave, rotulo) {
      this.$set(this.rotulosRestricoes, chave, rotulo);
    },

    irPara(indice) {
      if (indice < 0 || indice >= this.etapas.length) return;

      this.etapaAtual = indice;
    },

    etapaComErro(indice) {
      return this.erros.some((erro) => erro.etapa === indice);
    },

    statusEtapa(chave) {
      if (chave === "informacoes") {
        const canais = canaisDaRegra(this.regra).length;

        return `${this.regra.ativo ? "Ativa" : "Inativa"} · ${canais === 1 ? "1 canal" : `${canais} canais`}`;
      }

      if (chave === "regra") return rotuloTipoRegra(this.regra.tipo);

      if (chave === "restricoes") {
        const total = this.regra.restricoes.length;

        if (!total) return "Todos os clientes";

        return total === 1 ? "1 restrição" : `${total} restrições`;
      }

      return "Confira antes de salvar";
    },

    loadDependencias() {
      this.carregar("/admin/lista-dimensoes", "dimensoes");
      this.carregar("/admin/lista-produtos", "produtos");
      this.carregar("/admin/lista-promocoes", "promocoes");
    },

    carregar(url, destino) {
      this.$set(this.errosCarga, destino, false);

      get(url)
        .then((json) => {
          this[destino] = Array.isArray(json) ? json : [];
        })
        .catch(() => {
          this[destino] = [];
          this.$set(this.errosCarga, destino, true);
        });
    },

    recarregar(destino) {
      if (this.errosCarga[destino]) this.carregar(`/admin/lista-${destino}`, destino);
    },

    salvar() {
      this.mostrarErros = true;

      if (this.erros.length) {
        this.etapaAtual = this.erros[0].etapa;

        return;
      }

      if (canaisDaRegra(this.regra).length) {
        this.enviar();

        return;
      }

      this.$swal({
        title: "Nenhum canal selecionado",
        html: "<p>Sem canal, a regra fica cadastrada mas não é validada em nenhum sistema (B2B, Callcenter ou Força de vendas).</p>",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Salvar assim mesmo",
        cancelButtonText: "Revisar",
      })
        .then((res) => {
          if (res.value) {
            this.enviar();

            return;
          }

          this.etapaAtual = 0;
        })
        .catch(() => {});
    },

    enviar() {
      const payload = montarPayload(this.regra);

      if (payload.id) {
        this.update(payload);

        return;
      }

      this.create(payload);
    },
  },
};
</script>

<style scoped src="@/components/regras/regra-modal.css"></style>

<style scoped>
.regra-negocio-modal__lista-erros {
  margin: 4px 0 0;
  padding-left: 18px;
}

@media (max-width: 767.98px) {
  .regra-negocio-modal__corpo {
    padding: 20px 16px;
  }
}
</style>
